/**
 * The small lookup tables the whole console shares.
 *
 * Alarms, overrides and timeline rows reference people, teams and integrations
 * by id; the API expands them only on a detail response. Rather than have every
 * row fetch its own names, three long-lived queries hold the directory and each
 * page reads it from the cache. The lists are small (tens of rows) and change
 * rarely, so they are kept fresh for five minutes.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { integrationsApi, teamsApi, usersApi } from './api.js';
import type { Integration, Team, User } from './schemas.js';

const STALE_TIME = 5 * 60_000;

export interface Directory {
  users: Map<string, User>;
  teams: Map<string, Team>;
  integrations: Map<string, Integration>;
  userName: (id: string | null | undefined) => string | undefined;
  teamName: (id: string | null | undefined) => string | undefined;
  integrationName: (id: string | null | undefined) => string | undefined;
  /** True while any of the three is still loading. */
  isPending: boolean;
}

function byId<T extends { id: string }>(rows: T[] | undefined): Map<string, T> {
  return new Map((rows ?? []).map((row) => [row.id, row]));
}

export function useDirectory(): Directory {
  const users = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.listAll(),
    staleTime: STALE_TIME,
    retry: false,
  });
  const teams = useQuery({
    // Soft-deleted teams included: this map only ever resolves a name for an
    // alarm or timeline row that already references the team.
    queryKey: ['teams', 'all', 'with-deleted'],
    queryFn: () => teamsApi.listAllWithDeleted(),
    staleTime: STALE_TIME,
    retry: false,
  });
  const integrations = useQuery({
    queryKey: ['integrations', 'all'],
    queryFn: () => integrationsApi.listAll(),
    staleTime: STALE_TIME,
    retry: false,
  });

  return useMemo(() => {
    const userMap = byId(users.data);
    const teamMap = byId(teams.data);
    const integrationMap = byId(integrations.data);
    return {
      users: userMap,
      teams: teamMap,
      integrations: integrationMap,
      userName: (id) => (id ? (userMap.get(id)?.display_name ?? undefined) : undefined),
      teamName: (id) => (id ? (teamMap.get(id)?.name ?? undefined) : undefined),
      integrationName: (id) => (id ? (integrationMap.get(id)?.name ?? undefined) : undefined),
      isPending: users.isPending || teams.isPending || integrations.isPending,
    };
  }, [
    users.data,
    teams.data,
    integrations.data,
    users.isPending,
    teams.isPending,
    integrations.isPending,
  ]);
}

/** The same directory as plain `UserRef` rows, for `AlarmTimeline`. */
export function userRefs(directory: Directory): Map<string, User> {
  return directory.users;
}
